import _ from 'lodash-es';
import dompurify from 'dompurify';
import { v4 as uuidV4 } from 'uuid';

import {
  Skill,
  PilScoreByQueryQuery,
  ProgramAlumniQuery
} from '@/graphql/generated';
import { PredictionFilters } from '../types';

/**
 * Types
 */
type LocalStorageKey = 'isTourCompleted';
type ConfigReplaceKey = 'programName';

export const firstCharToUppercase = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1);

export const removeWhiteSpaces = (str: string) => str.replace(/\s+/g, '');

export const getInitials = (fullName: string) => {
  const name = fullName.split(' ')[0];
  const lastName = fullName.split(' ')[1];
  return `${name.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
};

export const getFullName = (fName: string, lName: string) =>
  `${fName} ${lName}`;

export const createPieChartDataFromPostPIL = (postPIL = 0) => [
  {
    name: 'post pil score',
    value: 100 - postPIL * 100
  },
  {
    name: 'pre pil score',
    value: postPIL * 100
  }
];

export const numberWithComma = (num: number) =>
  // eslint-disable-next-line security/detect-unsafe-regex
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const replaceLodashWithSpace = (value: string) =>
  value.replace('_', ' ');

export const storage = {
  get: <T>(key: LocalStorageKey): T | null => {
    const localStorageItem = localStorage.getItem(key);
    if (localStorageItem) {
      return JSON.parse(localStorageItem);
    }

    return null;
  },

  save: <T>(key: LocalStorageKey, value: T) =>
    localStorage.setItem(key, JSON.stringify(value)),

  clear: () => localStorage.clear(),

  remove: (key: LocalStorageKey) => localStorage.removeItem(key)
};

export const sanitizeHtml = (text: string) =>
  dompurify.sanitize(text, { ADD_ATTR: ['target'] });

export const replaceKeyWithValueInText = ({
  text,
  key,
  value,
  isHTML
}: {
  text: string;
  key: ConfigReplaceKey;
  value: string;
  isHTML?: boolean;
}) => {
  if (isHTML) {
    return sanitizeHtml(text.replace(`{${key}}`, value));
  }

  return text.replace(`{${key}}`, value);
};

export const highlightMatchedSkills = (
  primarySkills: Array<Pick<Skill, 'id' | 'name'>>,
  secondarySkills: Array<Pick<Skill, 'id' | 'name'>>
) => {
  const commonSkills: Array<Pick<Skill, 'id' | 'name'>> = _.intersectionBy(
    primarySkills,
    secondarySkills,
    'id'
  );

  return primarySkills.map(skill =>
    commonSkills.find(({ id }) => id === skill.id)
      ? { ...skill, isHighlighted: true }
      : skill
  );
};

export const PILScoreValuesToFilters = (
  values: Omit<PilScoreByQueryQuery['PILScoreByQuery'], 'postPIL'>
): Omit<PredictionFilters, 'isRelocating'> => ({
  years: values.years,
  majorId: values.major?.id,
  purposeId: values.purpose.id,
  subTrackId: values.subTrack.id,
  jobRoleId: values.jobRole.id,
  currentLocationId: values.currentLocation.id,
  destinationLocationId: values.destinationLocation.id
});

export const sortAlumniAndAddIds = (
  alumni: ProgramAlumniQuery['program']['alumni']
) =>
  alumni
    .map(alumnus => ({ ...alumnus, id: uuidV4() }))
    .sort((a, b) => b.PILBoost - a.PILBoost);

export function omitDeep<T extends { [key: string]: any }>(
  value: T,
  omitKeys: string[]
): T;

export function omitDeep<T extends { [key: string]: any }>(
  value: T[],
  omitKey: string[]
): T[];

export function omitDeep<T extends { [key: string]: any }>(
  value: T | T[],
  omitKeys: string[]
) {
  if (Array.isArray(value)) {
    return value.map(i => omitDeep(i, omitKeys));
  } else if (typeof value === 'object' && value !== null) {
    const keys = Object.keys(value);
    if (!keys.length) {
      return value;
    }

    return keys.reduce((newObject, k) => {
      if (omitKeys.includes(k.toLowerCase())) {
        return newObject;
      }
      return { ...newObject, [k]: omitDeep(value[k], omitKeys) };
    }, {} as T);
  }
  return value;
}
